import { useState, useEffect } from "react";
import { logger } from "@core/lib/logger";
import { supabase } from "@core/lib/supabase";
import { getGuestIdentity } from "@core/lib/guestIdentityService";
import { getInviteToken, isInviteOnly } from "@core/lib/inviteToken";
import { votingDisplayNameService } from "@core/services/votingDisplayNameService";
import {
  fetchPollResponses,
  invalidatePollResponses,
} from "@core/lib/pollResponsesCache";
import {
  submitPollResponse,
  deletePollResponse,
} from "@core/lib/edgeFunctionClient";

interface VoteData {
  optionId: string;
  voteType: "yes" | "no" | "maybe";
  timestamp: string;
  respondentName: string; // 🆕 Ajout pour l'affichage des noms
}

interface VoterInfo {
  name: string;
  email?: string;
}

interface UseVotingReturn {
  setVoterName(name: string): unknown;
  poll: any;
  loading: boolean;
  voterInfo: VoterInfo | null;
  hasVoted: boolean;
  votes: Record<string, VoteData[]>;
  currentVote: Record<string, "yes" | "no" | "maybe">;
  /** Vote de CE votant tel que persisté côté serveur (get_my_poll_response +
   *  mises à jour après submit). Sert au calcul non-doublonné des stats. */
  savedVote: Record<string, "yes" | "no" | "maybe">;
  userHasVoted: boolean;
  submitting: boolean;
  setVoterInfo: (info: VoterInfo) => void;
  updateVote: (optionId: string, voteType: "yes" | "no" | "maybe") => void;
  removeVote: (optionId: string) => void;
  updateLocalVote: (optionId: string, voteType: "yes" | "no" | "maybe") => void;
  getVoteStats: () => { total: number; byOption: Record<string, number> };
  getBestOption: () => string | null;
  hasVotes: boolean;
  totalVotes: number;
  submitVote: (
    optionId: string,
    voteType: "yes" | "no" | "maybe",
  ) => Promise<void>;
  submitAllVotes: (
    votes: Record<string, "yes" | "no" | "maybe">,
    voterName: string,
    email?: string,
  ) => Promise<string | null>;
  isSubmitting: boolean;
  error: string | null;
  // Fonctionnalité d'affichage des noms
  getVoterDisplayNames: () => Promise<
    Map<string, { displayName: string; isGuest: boolean }>
  >;
  getCurrentUserDisplayName: () => Promise<string>;
}

export default function useVoting(
  pollId: string,
  pollOverride?: any,
): UseVotingReturn {
  const [poll, setPoll] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [voterInfo, setVoterInfo] = useState<VoterInfo | null>(null);
  const [hasVoted, setHasVoted] = useState(false);
  const [votes, setVotes] = useState<Record<string, VoteData[]>>({});
  const [currentVote, setCurrentVote] = useState<
    Record<string, "yes" | "no" | "maybe">
  >({});
  const [userHasVoted, setUserHasVoted] = useState(false);
  // Vote persisté côté serveur pour CE votant (cf. UseVotingReturn.savedVote)
  const [savedVote, setSavedVote] = useState<
    Record<string, "yes" | "no" | "maybe">
  >({});
  // Nom saisi par CE votant (response_data.voterName de sa propre réponse) —
  // prioritaire sur le pseudonyme créatif dérivé du fingerprint.
  const [ownVoterName, setOwnVoterName] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Validation des votes
  const validateVote = (voteType: "yes" | "no" | "maybe"): boolean => {
    return voteType === "yes" || voteType === "no" || voteType === "maybe";
  };

  const setVoterInfoHandler = (info: VoterInfo) => {
    setVoterInfo(info);
  };

  const updateLocalVote = (
    optionId: string,
    voteType: "yes" | "no" | "maybe",
  ) => {
    logger.debug("USE_VOTING - Updating local vote only (no save)", "vote", {
      optionId,
      voteType,
    });
    setCurrentVote((prev) => ({ ...prev, [optionId]: voteType }));
  };

  const updateVote = (optionId: string, voteType: "yes" | "no" | "maybe") => {
    updateLocalVote(optionId, voteType);
  };

  const removeVote = (optionId: string) => {
    setCurrentVote((prev) => {
      const newVotes = { ...prev };
      delete newVotes[optionId];
      return newVotes;
    });
  };

  const getVoteStats = () => {
    const total = Object.values(votes).reduce(
      (sum, voteArray) => sum + voteArray.length,
      0,
    );
    const byOption = Object.entries(votes).reduce(
      (acc, [optionId, voteArray]) => {
        acc[optionId] = voteArray.length;
        return acc;
      },
      {} as Record<string, number>,
    );
    return { total, byOption };
  };

  const getBestOption = () => {
    const stats = getVoteStats();
    const bestOption = Object.entries(stats.byOption).reduce(
      (best, [optionId, count]) =>
        count > best.count ? { optionId, count } : best,
      { optionId: "", count: 0 },
    );
    return bestOption.optionId || null;
  };

  const hasVotes = Object.keys(votes).length > 0;
  const totalVotes = Object.values(votes).reduce(
    (sum, voteArray) => sum + voteArray.length,
    0,
  );

  useEffect(() => {
    const loadPoll = async () => {
      try {
        setLoading(true);
        setError(null);

        // RPC SECURITY DEFINER : lockdown RLS anon (bug hunt 2026-04-23).
        // ⚡ Passe par le cache d'in-flight (pollResponsesCache) pour
        // dédupliquer avec usePollVotes qui charge le même set côté Voting.tsx.
        const { data: votesData, error: votesError } =
          await fetchPollResponses(pollId);

        if (votesError) throw votesError;

        // Logs détaillés pour voir tous les votes
        logger.info("USE_VOTING - Loading all votes from Supabase", "vote", {
          pollId,
          totalVotesFromDB: votesData?.length || 0,
          votesData: votesData?.map((v) => ({
            id: v.id,
            respondent_name: v.respondent_name,
            response_data: v.response_data,
            created_at: v.created_at,
            voterName: v.response_data?.voterName || "NO_NAME",
          })),
        });

        // Transformer les données en format local
        // currentVote n'est PAS dérivé de l'agrégat: il représente le vote
        // de l'utilisateur courant et est rempli plus bas après reconnaissance
        // par fingerprint (sinon nouveau visiteur verrait les cases pré-cochées
        // avec le dernier vote de n'importe quel autre votant).
        const votesMap: Record<string, VoteData[]> = {};

        votesData?.forEach((vote) => {
          if (vote.response_data) {
            Object.entries(vote.response_data).forEach(
              ([optionId, voteType]) => {
                if (optionId === "voterName") return;
                if (!votesMap[optionId]) {
                  votesMap[optionId] = [];
                }
                votesMap[optionId].push({
                  optionId,
                  voteType: voteType as "yes" | "no" | "maybe",
                  timestamp: vote.created_at,
                  respondentName: vote.respondent_name, // 🆕 Ajout du nom du votant
                });
              },
            );
          }
        });

        // Log après transformation
        logger.info("USE_VOTING - Votes transformed to local format", "vote", {
          totalOptions: Object.keys(votesMap).length,
          votesByOption: Object.entries(votesMap).map(
            ([optionId, voteArray]) => ({
              optionId,
              voteCount: voteArray.length,
              votes: voteArray.map((v) => ({
                respondentName: v.respondentName,
                voteType: v.voteType,
                timestamp: v.timestamp,
              })),
            }),
          ),
        });

        setVotes(votesMap);
        setCurrentVote({});

        // Vérifier si l'utilisateur actuel a déjà voté.
        //
        // ⚠️ Ancienne approche (supprimée) : filtrer `votesData` (issu de
        // `fetchPollResponses`/`get_poll_responses`, la liste PUBLIQUE utilisée
        // pour l'affichage des résultats) en comparant `respondent_name` au
        // fingerprint client. Deux failles cumulées la rendaient inopérante :
        //  1. `get_poll_responses` renvoie `respondent_name: null` (masqué
        //     côté RPC pour ne pas exposer le fingerprint d'un votant dans les
        //     résultats publics) — la comparaison échouait TOUJOURS.
        //  2. Même corrigé, le fingerprint envoyé à l'écriture
        //     (`identity.stableFingerprint || identity.fingerprint`, plus bas
        //     dans ce fichier) diffère du fingerprint instable utilisé ici.
        // Résultat observé : après un vote, le compteur des résultats reflète
        // bien la réponse (agrégat sans filtre d'identité) mais les cases de
        // l'utilisateur reviennent vides au reload/revote. Bug rapporté par
        // Julien le 2026-07-09.
        //
        // Fix : appeler `get_my_poll_response`, la RPC SECURITY DEFINER dédiée
        // qui résout la ligne du CALLER côté serveur (COALESCE(auth.uid(),
        // p_fingerprint) puis égalité exacte sur `stable_fingerprint`, la
        // colonne complète — pas de troncature ni de champ redacté).
        const identity = await getGuestIdentity();
        const { data: myResponses, error: myResponseError } =
          await supabase.rpc("get_my_poll_response", {
            p_poll_id: pollId,
            p_fingerprint: identity.stableFingerprint || null,
          });

        if (myResponseError) {
          logger.warn("USE_VOTING - get_my_poll_response failed", "vote", {
            error: myResponseError.message,
          });
        }

        const finalUserVote = (myResponses || [])[0] || null;

        logger.info("USE_VOTING - User recognition result", "vote", {
          hasVoted: !!finalUserVote,
          userVote: finalUserVote
            ? {
                id: finalUserVote.id,
                voterName: finalUserVote.response_data?.voterName || "NO_NAME",
                response_data: finalUserVote.response_data,
              }
            : null,
        });

        setHasVoted(!!finalUserVote);
        setUserHasVoted(!!finalUserVote);

        // Si déjà voté, charger le vote précédent dans currentVote
        if (finalUserVote && finalUserVote.response_data) {
          const voteData = finalUserVote.response_data;
          if (
            typeof voteData.voterName === "string" &&
            voteData.voterName.trim()
          ) {
            setOwnVoterName(voteData.voterName.trim());
          }
          const voteKeys = Object.keys(voteData).filter(
            (k) => k !== "voterName",
          );
          const currentVoteMap = {};

          voteKeys.forEach((optionId) => {
            currentVoteMap[optionId] = voteData[optionId];
          });

          setCurrentVote(currentVoteMap);
          setSavedVote(currentVoteMap);

          logger.info(
            "USE_VOTING - Loaded previous vote for returning user",
            "vote",
            {
              currentVoteMap,
              voterName: voteData.voterName,
            },
          );
        }

        // Mock data pour le poll (à remplacer avec vrai appel API)
        setPoll({
          id: pollId,
          title: "Sondage de dates",
          options: [
            { id: "1", title: "Option 1", description: "Description 1" },
            { id: "2", title: "Option 2", description: "Description 2" },
          ],
        });

        setLoading(false);
      } catch (err) {
        logger.error("Error loading poll:", err);
        setError("Erreur lors du chargement du sondage");
        setLoading(false);
      }
    };

    loadPoll();
  }, [pollId, pollOverride]);

  const submitVote = async (
    optionId: string,
    voteType: "yes" | "no" | "maybe",
    voterName?: string, // Nouveau paramètre pour le nom du votant
  ) => {
    try {
      // ð³ Protection contre les soumissions multiples
      if (isSubmitting) {
        logger.warn(
          "USE_VOTING - Vote submission already in progress, skipping",
          "vote",
          {
            optionId,
            voteType,
            voterName,
            stackTrace: new Error().stack?.substring(0, 500),
          },
        );
        return;
      }

      logger.info("USE_VOTING - submitVote called", "vote", {
        optionId,
        voteType,
        voterName,
        isSubmitting,
        stackTrace: new Error().stack?.substring(0, 500),
      });

      setIsSubmitting(true);
      setError(null);

      // Validation du vote
      if (!validateVote(voteType)) {
        throw new Error(
          "Type de vote invalide. Seuls 'yes', 'no', 'maybe' sont autorisés.",
        );
      }

      // Récupérer l'identité de l'utilisateur (fingerprint pour les invités)
      const identity = await getGuestIdentity();
      // Préférer le fingerprint STABLE (résistant aux màj navigateur/GPU) ;
      // fallback sur le fingerprint instable si le stable est indisponible.
      const fullFingerprint =
        identity.stableFingerprint || identity.fingerprint;

      // Utiliser le fingerprint comme identifiant unique (respondent_name)
      // mais stocker le nom du votant dans response_data pour l'affichage
      const storedVoterName = voterName || (useVoting as any)._tempVoterName;
      const respondentName = fullFingerprint.substring(0, 32);

      if (storedVoterName && storedVoterName.trim()) {
        logger.info(
          "USE_VOTING - Using fingerprint as ID with stored display name",
          "vote",
          {
            respondentName,
            voterDisplayName: storedVoterName.trim(),
            fullFingerprint: fullFingerprint.substring(0, 16) + "...",
          },
        );
      } else {
        logger.info(
          "USE_VOTING - Using fingerprint as respondent name",
          "vote",
          {
            respondentName,
            fullFingerprint: fullFingerprint.substring(0, 16) + "...",
          },
        );
      }

      logger.info("USE_VOTING - Submitting vote", "vote", {
        pollId,
        optionId,
        voteType,
        fullFingerprint: fullFingerprint.substring(0, 16) + "...",
        truncatedFingerprint: respondentName,
        deviceId: identity.deviceId?.substring(0, 16) + "...",
      });

      // Bug hunt Nuit 2 (2026-05-05) — S1 : passer par submit_poll_response
      // qui vérifie status, expiry et applique le rate-limit 5/min.
      // INSERT direct révoqué côté DB (migration 20260505_harden_poll_responses_writes).
      // Phase 1 security fix #859: Use Edge Function wrapper instead of direct RPC
      let newData, upsertError;
      try {
        newData = await submitPollResponse({
          poll_id: pollId,
          response_data: { [optionId]: voteType },
          voter_name: storedVoterName || voterName || "NO_NAME",
          response_type: "date",
          fingerprint: identity.stableFingerprint || undefined,
          device_id: identity.deviceId || undefined,
          invite_token: getInviteToken(pollId) || undefined,
          invite_required: isInviteOnly(pollId) || undefined,
        });
        upsertError = null;
      } catch (err) {
        upsertError = err instanceof Error ? err : new Error(String(err));
        newData = null;
      }

      if (upsertError) throw upsertError;

      // Invalider le cache pour que le prochain refetch (real-time ou hook
      // sœur) renvoie la donnée à jour et non un hit du cache 500ms.
      invalidatePollResponses(pollId);

      logger.info("USE_VOTING - Vote upserted successfully", "vote", {
        optionId,
        voteType,
        respondentName,
        newDataId: newData?.id,
        insertedResponseData: newData?.response_data,
      });

      logger.info("USE_VOTING - Vote saved successfully", "vote", {
        optionId,
        voteType,
        respondentName,
        voterName: storedVoterName,
      });

      setHasVoted(true);
      setSavedVote((prev) => ({ ...prev, [optionId]: voteType }));
      setIsSubmitting(false);
    } catch (err) {
      logger.error("USE_VOTING - Error submitting vote", "vote", {
        error: err,
        errorMessage: err instanceof Error ? err.message : String(err),
        errorStack: err instanceof Error ? err.stack : undefined,
        optionId,
        voteType,
        pollId,
      });

      setError(
        err instanceof Error
          ? err.message
          : "Erreur lors de la soumission du vote",
      );
      setIsSubmitting(false);
    }
  };

  // Soumettre tous les votes en une seule opération atomique (évite la race condition)
  const submitAllVotes = async (
    votesMap: Record<string, "yes" | "no" | "maybe">,
    voterName: string,
    email?: string,
  ): Promise<string | null> => {
    try {
      setIsSubmitting(true);
      setError(null);

      const identity = await getGuestIdentity();
      const respondentName = (
        identity.stableFingerprint || identity.fingerprint
      ).substring(0, 32);
      const storedVoterName = voterName.trim() || "NO_NAME";

      // Bug hunt Nuit 2 (2026-05-05) — S1 : idem submitVote, passer par submit_poll_response.
      // p_email persiste respondent_email côté serveur : c'est la SEULE source
      // du destinataire de l'email de confirmation (#810), jamais un email client.
      // Phase 1 security fix #859: Use Edge Function wrapper instead of direct RPC
      let respRow, upsertError;
      try {
        respRow = await submitPollResponse({
          poll_id: pollId,
          response_data: votesMap,
          voter_name: storedVoterName,
          response_type: "date",
          fingerprint: identity.stableFingerprint || undefined,
          device_id: identity.deviceId || undefined,
          email: email?.trim() || undefined,
          // Sondage sur invitation : sans ce jeton le serveur refuse le vote.
          invite_token: getInviteToken(pollId) || undefined,
          invite_required: isInviteOnly(pollId) || undefined,
        });
        upsertError = null;
      } catch (err) {
        upsertError = err instanceof Error ? err : new Error(String(err));
        respRow = null;
      }

      if (upsertError) throw upsertError;

      // Invalider le cache après upsert (cf. submitVote)
      invalidatePollResponses(pollId);

      setHasVoted(true);
      setSavedVote(votesMap);
      setIsSubmitting(false);
      const row = Array.isArray(respRow) ? respRow[0] : respRow;
      return (row as { id?: string } | null)?.id ?? null;
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Erreur lors de la soumission du vote",
      );
      setIsSubmitting(false);
      throw err;
    }
  };

  // Définir le nom du votant pour la soumission suivante
  const setVoterName = (name: string) => {
    logger.info("USE_VOTING - Setting voter name", "vote", {
      voterName: name,
    });
    // Stocker temporairement le nom pour l'utiliser dans submitVote
    (useVoting as any)._tempVoterName = name.trim();
  };

  // 🆕 Fonction pour récupérer les noms d'affichage des votants
  const getVoterDisplayNames = async () => {
    // Extraire tous les respondent_name uniques des votes
    const allRespondentNames = new Set<string>();
    Object.values(votes).forEach((voteList) => {
      voteList.forEach((vote) => {
        if (vote.respondentName) {
          allRespondentNames.add(vote.respondentName);
        }
      });
    });

    // Récupérer les infos d'affichage via le service
    const displayNames =
      await votingDisplayNameService.getMultipleVoterDisplayInfos(
        Array.from(allRespondentNames),
      );

    // Transformer en Map simple pour le composant
    const result = new Map<string, { displayName: string; isGuest: boolean }>();
    displayNames.forEach((info, respondentName) => {
      result.set(respondentName, {
        displayName: info.displayName,
        isGuest: info.isGuest,
      });
    });

    return result;
  };

  const getCurrentUserDisplayName = async () => {
    // Un utilisateur connecté est toujours affiché sous l'identité de son
    // compte — jamais sous le pseudonyme créatif dérivé du fingerprint anonyme
    // (bug rapporté 2026-07-23 : Julien connecté reconnu comme "Lion Doré").
    try {
      const { data } = await supabase.auth.getUser();
      const authName =
        data?.user?.user_metadata?.full_name ||
        data?.user?.user_metadata?.name ||
        data?.user?.email;
      if (authName) return authName as string;
    } catch {
      // Session indisponible — fallback fingerprint ci-dessous
    }
    // Votant anonyme reconnu : préférer le nom qu'il a saisi (voterName de sa
    // propre réponse) au pseudonyme créatif "Lion Doré #X".
    if (ownVoterName) return ownVoterName;
    const identity = await getGuestIdentity();
    const respondentName = (
      identity.stableFingerprint || identity.fingerprint
    ).substring(0, 32);

    try {
      const displayInfo =
        await votingDisplayNameService.getVoterDisplayInfo(respondentName);
      logger.debug("USE_VOTING - Current user display name retrieved", "vote", {
        respondentName,
        displayName: displayInfo.displayName,
        actualName: displayInfo.actualName,
      });
      return displayInfo.displayName;
    } catch (error) {
      logger.error("Error getting current user display name", "vote", {
        error,
      });
      return "Vous";
    }
  };

  return {
    poll,
    loading,
    voterInfo,
    hasVoted,
    savedVote,
    votes,
    currentVote,
    userHasVoted,
    submitting,
    error,
    isSubmitting,
    submitVote,
    submitAllVotes,
    setVoterName,
    getVoterDisplayNames,
    getCurrentUserDisplayName,
    setVoterInfo: (info: VoterInfo) => {
      setVoterInfo(info);
    },
    updateLocalVote,
    updateVote: (optionId: string, voteType: "yes" | "no" | "maybe") => {
      setCurrentVote((prev) => ({ ...prev, [optionId]: voteType }));
    },
    removeVote: (optionId: string) => {
      setCurrentVote((prev) => {
        const newVotes = { ...prev };
        delete newVotes[optionId];
        return newVotes;
      });
    },
    getVoteStats: () => {
      // Implementation for getVoteStats
      const byOption: Record<string, number> = {};
      let total = 0;

      Object.entries(votes).forEach(([optionId, voteList]) => {
        const optionCount = voteList.length;
        byOption[optionId] = optionCount;
        total += optionCount;
      });

      return { total, byOption };
    },
    getBestOption: () => {
      const stats = { yes: 0, no: 0, maybe: 0 };
      Object.values(votes).forEach((voteList) => {
        voteList.forEach((vote) => {
          if (vote.voteType in stats) {
            stats[vote.voteType as keyof typeof stats]++;
          }
        });
      });
      return stats.yes > 0
        ? "yes"
        : stats.no > 0
          ? "no"
          : stats.maybe > 0
            ? "maybe"
            : null;
    },
    hasVotes: Object.keys(votes).length > 0,
    totalVotes: Object.values(votes).reduce(
      (sum, voteList) => sum + voteList.length,
      0,
    ),
  };
}

export { useVoting };
